<script setup lang="ts">
import { computed } from 'vue'
import storytellerLogo from '@/assets/icons/Storyteller.svg'

type AvatarSize = 'sm' | 'md' | 'lg'

const props = withDefaults(defineProps<{
  name?: string
  size?: AvatarSize
}>(), {
  name: '',
  size: 'md',
})

// Deterministic hue from name string
const hue = computed(() => {
  const str = props.name || ''
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash) % 360
})

const bgColor = computed(() => `hsl(${hue.value}, 35%, 22%)`)
const textColor = computed(() => `hsl(${hue.value}, 60%, 72%)`)

const initials = computed(() => {
  const str = (props.name || '').trim()
  if (!str) return '?'
  const words = str.split(/\s+/)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return str.slice(0, 2).toUpperCase()
})

const sizeClass = computed(() => `avatar-fallback--${props.size}`)
</script>

<template>
  <div
    class="avatar-fallback"
    :class="sizeClass"
    :style="{ background: bgColor, color: textColor }"
    role="img"
    :aria-label="name || 'Avatar'"
  >
    <span class="avatar-fallback__initials">{{ initials }}</span>

    <!-- Storyteller logo watermark behind initials -->
    <img
      class="avatar-fallback__watermark"
      :src="storytellerLogo"
      alt=""
      aria-hidden="true"
    />
  </div>
</template>

<style scoped>
.avatar-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  flex-shrink: 0;
  font-weight: 700;
  letter-spacing: 0.02em;
  user-select: none;
}

.avatar-fallback--sm {
  width: 28px;
  height: 28px;
  font-size: 0.65rem;
}

.avatar-fallback--md {
  width: 40px;
  height: 40px;
  font-size: 0.85rem;
}

.avatar-fallback--lg {
  width: 64px;
  height: 64px;
  font-size: 1.2rem;
}

.avatar-fallback__initials {
  position: relative;
  z-index: 1;
}

.avatar-fallback__watermark {
  position: absolute;
  width: 75%;
  height: 75%;
  bottom: -8%;
  right: -8%;
  object-fit: contain;
  pointer-events: none;
  opacity: 0.1;
  filter: brightness(0) invert(1);
}
</style>
